import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { ProcessingStateLine, type ProcessingStateCopy } from "documents/classification/_views/document-processing-state";
import { FileScanStatus } from "messaging/attachments/file-info/file-scan-status";
import type MessageFileInfo from "messaging/attachments/file-info/message-file-info";

const SubjectMatterChips = styled("span")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.25),
}));

/**
 * A subject matter in the color of the text around it, as the processing line is (#7127). A chip's
 * own `text.primary` is near black, which is unreadable on a sent bubble's blue.
 */
const SubjectMatterChip = styled(Chip)(() => ({
  color: "inherit",
}));

/**
 * What the line says once the scan has cleared the file, by how far classification got.
 *
 * Unlike a document's, a chat file's download does not wait for classification: a file not classified
 * yet, or one classification failed on, still downloads. What classification finds can refuse it, though:
 * a reader without a grant for one of its subject matters cannot download it (#6993), which
 * `MessageFileInfo.withheldReason` says. So no line here says the file is withheld.
 */
const classificationCopy: Record<DocumentClassificationStatus, ProcessingStateCopy | null> = {
  [DocumentClassificationStatus.Pending]: {
    label: "Processing",
    color: "default",
    explanation:
      "This file is being processed. This usually takes under a minute. You can download it now, and you "
      + "do not need to send it again.",
  },
  [DocumentClassificationStatus.Failed]: {
    label: "Processing failed",
    color: "warning",
    explanation:
      "Processing did not succeed, so the assistant cannot search this file or use it to answer questions. "
      + "You can still download it. Sending the file again can help.",
  },
  // A classified file shows its subject matters instead of a line.
  [DocumentClassificationStatus.Succeeded]: null,
  [DocumentClassificationStatus.Unindexable]: {
    label: "No text to search",
    color: "default",
    explanation:
      "No text could be read from this file. Usually it is a scan or a photo. You can download it as "
      + "usual, but the assistant cannot search it. Sending the same file again will not change this.",
  },
};

/**
 * Under a chat file: how far its processing got, and once it is classified, its subject matters
 * (#6877). Nothing for a file whose payload does not say, such as one sent before chat files were
 * classified.
 *
 * Nothing either while the malware scan has not cleared the file: the file's withheld reason says
 * that already (#6916), and classification waits for the scan.
 */
export default function AttachmentProcessingState(props: Readonly<{ file: MessageFileInfo }>) {
  const { scanStatus, classificationStatus, subjectMatters } = props.file;
  if (scanStatus !== undefined && scanStatus !== FileScanStatus.Clean) return null;
  if (classificationStatus === undefined) return null;

  const copy = classificationCopy[classificationStatus];
  // In the bubble's own text color: a document page's grey on a sent bubble's blue is unreadable.
  if (copy) return <ProcessingStateLine {...copy} inheritColor />;
  if (!subjectMatters?.length) return null;

  return (
    <SubjectMatterChips aria-label="Subject matter">
      {subjectMatters.map((subjectMatter) => (
        <SubjectMatterChip key={subjectMatter} size="small" label={subjectMatter} />
      ))}
    </SubjectMatterChips>
  );
}
